import {
    ComponentProps,
    ReactElement,
    useEffect,
    useRef,
    useState,
} from 'react';

import KeysOfType from '../../types/DataStructure/KeysOfType';

import ListRenderer from './components/ListRenderer';

export type RenderOption<TOption extends Record<string, any>> = (
    props: {
        option: TOption;
        selected: boolean;
        onSelect: () => void;
    },
    index: number
) => ReactElement;

export type ListPickerProps<
    TOption extends Record<string, any>,
    TValuePropertyName extends KeysOfType<TOption, string>,
> = {
    options: Array<TOption>;
    renderOption: RenderOption<TOption>;
    valueProperty: TValuePropertyName;
    defaultValue?: TOption[TValuePropertyName];
    value?: TOption[TValuePropertyName];
    onChange?: (
        value: TOption[TValuePropertyName] | undefined,
        option: TOption
    ) => void;
} & Pick<
    ComponentProps<typeof ListRenderer>,
    | 'className'
    | 'label'
    | 'errors'
    | 'name'
    | 'inline'
    | 'inlineAlignment'
    | 'children'
>;

const ListPicker = <
    TOption extends Record<string, any>,
    TValuePropertyName extends KeysOfType<TOption, string>,
>({
    valueProperty,
    options,
    onChange,
    renderOption,
    value,
    defaultValue,
    children,
    ...rendererProps
}: ListPickerProps<TOption, TValuePropertyName>) => {
    const hasExternalValue = typeof value !== 'undefined';
    const [internalValue, setInternalValue] = useState(value ?? defaultValue);
    const renderValue = hasExternalValue ? value : internalValue;

    // Create a ref to hold the previous value of the prop
    const prevValue = useRef(value);

    useEffect(() => {
        if (value !== prevValue.current) {
            // The component is no longer controlled
            setInternalValue(undefined);

            // Update the previous value of the prop
            prevValue.current = value;
        }
    }, [value]);

    /** Prevent keeping an invalid internal value */
    useEffect(() => {
        const currentOption = options.find(
            (option) => option[valueProperty] === internalValue
        );

        if (!hasExternalValue && !currentOption) {
            setInternalValue(undefined);
        }
    }, [options, valueProperty, internalValue, hasExternalValue]);

    const optionsAsChildren = options.map((option, index) => {
        const onSelect = () => {
            const newValue =
                option[valueProperty] === renderValue
                    ? undefined
                    : option[valueProperty];

            if (!hasExternalValue) {
                setInternalValue(newValue);
            }

            onChange && onChange(newValue, option);
        };

        const component = renderOption(
            {
                onSelect: onSelect,
                option: option,
                selected: option[valueProperty] === renderValue,
            },
            index
        );

        if (!component.key) {
            return <span key={option[valueProperty]}>{component}</span>;
        }

        return component;
    });

    return (
        <ListRenderer value={renderValue} {...rendererProps} noWrap>
            {optionsAsChildren}
            {children}
        </ListRenderer>
    );
};

export default ListPicker;
